import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { Receipt } from 'lucide-react'
import { AlertBanner } from '../components/ui/AlertBanner'
import { KPICard } from '../components/ui/KPICard'
import { PageHeader } from '../components/ui/PageHeader'
import { YearSelector } from '../components/ui/YearSelector'
import { DsoTrendChart } from '../components/dashboard/DsoTrendChart'
import { AgingARBars } from '../components/dashboard/AgingARBars'
import { CashForecastChart } from '../components/dashboard/CashForecastChart'
import { useBillingKPIs, useAvailableYears } from '../hooks/useKPIs'
import { useGlobalFilters } from '../contexts/GlobalFiltersContext'
import { useBUContext } from '../contexts/BUContext'

function formatEur(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function PanelHeader({
  label,
  children,
}: {
  label: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{label}</p>
      </div>
      {children}
    </div>
  )
}

export function Billing() {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number | 'cumulative'>(currentYear)
  const { data: availableYears = [currentYear] } = useAvailableYears()
  const { filters, apiDateFrom, apiDateTo } = useGlobalFilters()
  const { selectedBuId } = useBUContext()
  const { data: kpis, isLoading, error } = useBillingKPIs({
    year: selectedYear,
    apiDateFrom,
    apiDateTo,
    customer: filters.customer,
    buId: selectedBuId,
  })

  const dsoDelta =
    kpis?.dso_days_prev != null
      ? ((kpis.dso_days - kpis.dso_days_prev) / kpis.dso_days_prev) * 100
      : undefined

  const subtitle = kpis
    ? `Aggiornato al ${format(new Date(kpis.computed_at), "d MMM yyyy 'ore' HH:mm", { locale: it })}`
    : format(new Date(), 'd MMM yyyy', { locale: it })

  return (
    <section aria-label="Billing & Crediti" className="space-y-6">
      <PageHeader
        title="Billing & Crediti"
        subtitle={subtitle}
        icon={Receipt}
        iconColor="from-cyan-500 to-blue-500"
        coveragePct={kpis?.coverage_score_pct}
        loading={isLoading}
        actions={
          <YearSelector
            years={availableYears}
            value={selectedYear}
            onChange={setSelectedYear}
            loading={isLoading}
            disabled={!!(apiDateFrom && apiDateTo)}
          />
        }
      />

      {error && (
        <AlertBanner
          severity="critical"
          message="Impossibile caricare i KPI Billing & Crediti. Riprovare."
        />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="stagger-1">
          <KPICard
            label="DSO"
            value={kpis ? `${kpis.dso_days.toFixed(0)} gg` : '—'}
            delta={dsoDelta !== undefined
              ? `${dsoDelta >= 0 ? '+' : ''}${dsoDelta.toFixed(1)}% vs mese prec.`
              : undefined}
            deltaPositive={dsoDelta !== undefined ? dsoDelta <= 0 : undefined}
            status={kpis
              ? kpis.dso_days <= 30 ? 'positive'
                : kpis.dso_days <= 60 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Days Sales Outstanding"
          />
        </div>

        <div className="stagger-2">
          <KPICard
            label="Crediti totali"
            value={kpis ? formatEur(kpis.total_outstanding) : '—'}
            status="neutral"
            loading={isLoading}
            subtext="Fatture aperte non incassate"
          />
        </div>

        <div className="stagger-3">
          <KPICard
            label="Crediti scaduti"
            value={kpis ? formatEur(kpis.total_overdue) : '—'}
            status={kpis
              ? kpis.overdue_rate_pct <= 5 ? 'positive'
                : kpis.overdue_rate_pct <= 15 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext={kpis ? `${formatPct(kpis.overdue_rate_pct)} del totale` : undefined}
          />
        </div>

        <div className="stagger-4">
          <KPICard
            label="Giorni medi pagamento"
            value={kpis ? `${kpis.avg_payment_days.toFixed(0)} gg` : '—'}
            status={kpis
              ? kpis.avg_payment_days <= 30 ? 'positive'
                : kpis.avg_payment_days <= 60 ? 'warning' : 'critical'
              : 'neutral'}
            loading={isLoading}
            subtext="Tempo medio di incasso"
          />
        </div>
      </div>

      {/* DSO trend + Aging AR */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* DSO trend — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Trend DSO — ultimi 12 mesi">
            <div className="flex items-center gap-3 text-[10px] font-medium text-[--km-text-muted]">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-px w-5 border-t border-dashed border-emerald-500/70" />
                Soglia 30gg
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-px w-5 border-t border-dashed border-amber-500/70" />
                Soglia 60gg
              </span>
            </div>
          </PanelHeader>
          <div className="h-60">
            <DsoTrendChart data={kpis?.dso_trend ?? []} />
          </div>
        </div>

        {/* Aging AR — 1/3 */}
        <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Aging AR — 4 fasce" />

          {isLoading ? (
            <div className="space-y-5 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-2">
                  <div className="h-2.5 w-36 rounded-full bg-[--km-surface-3]" />
                  <div className="h-1.5 w-full rounded-full bg-[--km-surface-3]" />
                </div>
              ))}
            </div>
          ) : kpis ? (
            <AgingARBars aging={kpis.aging_ar} />
          ) : null}
        </div>
      </div>

      {/* Cash forecast 13 settimane */}
      <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
        <PanelHeader label="Cash collection forecast — 13 settimane">
          <div className="flex items-center gap-3 text-[10px] font-medium text-[--km-text-muted]">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-500" />
              Incasso atteso
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-5 bg-purple-500" />
              Cumulativo
            </span>
          </div>
        </PanelHeader>
        <div className="h-60">
          <CashForecastChart data={kpis?.cash_forecast_13w ?? []} />
        </div>
      </div>

      {kpis && kpis.coverage_score_pct < 80 && (
        <AlertBanner
          severity="warning"
          message={`Copertura dati: ${kpis.coverage_score_pct.toFixed(0)}% dei KPI calcolabili. Caricare più dati per ottenere un quadro completo.`}
        />
      )}
    </section>
  )
}
